import { type ImpersonatorSession, type UserSession } from '@solinkcorp/authentication';
import settings from '@solinkcorp/settings';
import { DEFAULT_LOCALE } from '@solinkcorp/vd-common';
import { IS_PRODUCTION } from 'src/environment';
import pkg from '../../../package.json';
import { type AnalyticsEvent, loadTracker, overrideModule, remapUserFeatures } from './common';

// There's supposed to be no lodash in the core, but lodash's kebabCase wouldn't
// be trivial to recreate, and by importing it from lodash/kebabCase we avoid
// importing all 500K of lodash.

import kebabCase from 'lodash/kebabCase';

const { version } = pkg;

interface Intercom {
  (command: 'shutdown'): void;
  (command: 'boot', properties: Record<string, unknown>): void;
  (command: 'trackEvent', eventName: string, properties: Record<string, unknown>): void;
}

let intercomInstance: Intercom | undefined;

const generateIntercomMock = (): Intercom => {
  console.debug('Analytics: Using Mock Intercom');
  const mockIntercom: Intercom = (
    type: 'shutdown' | 'boot' | 'trackEvent',
    nameOrDetail?: string | Record<string, unknown>,
    metadata?: Record<string, unknown>,
  ) => {
    if (type === 'trackEvent') {
      console.debug(
        `Analytics: intercom [actionType="${type}", eventName="${nameOrDetail}", metadata=${JSON.stringify(
          metadata,
        )}]`,
      );
    } else if (type === 'boot') {
      console.debug(
        `Analytics: intercom [actionType="${type}", object=${JSON.stringify(nameOrDetail)}]`,
      );
    } else {
      console.debug(`Analytics: intercom [actionType="${type}"]`);
    }
  };

  return mockIntercom;
};

let intercomErrorShown = false;
function isIntercomLoaded(intercomInstance: Intercom | undefined): intercomInstance is Intercom {
  const isLoaded = !!intercomInstance;
  if (!isLoaded && IS_PRODUCTION && !intercomErrorShown) {
    console.error(`Intercom is not loaded`);
    intercomErrorShown = true;
  }
  return isLoaded;
}

export async function _loadIntercom(
  user: UserSession,
  impersonator: ImpersonatorSession | undefined,
): Promise<Intercom | undefined> {
  // Disable Intercom when impersonating non- @solinkcorp.com or @customer.com users
  if (impersonator && !user.isSolinkUser && !user.isDemoUser) {
    console.debug('Do not load Intercom during impersonation...');
    return undefined;
  }

  if (!intercomInstance) {
    if (overrideModule()) {
      intercomInstance = generateIntercomMock();
    } else {
      intercomInstance = await loadTracker('Intercom', () => (window as any).Intercom as Intercom);
    }
  }

  return intercomInstance;
}

export function _disableIntercom(): void {
  try {
    if (isIntercomLoaded(intercomInstance)) {
      intercomInstance('shutdown');
      intercomInstance = undefined;
    } else if ((window as any).Intercom) {
      // In case this.intercom has not yet been initialized
      (window as any).Intercom('shutdown');
    }
  } catch (err) {
    console.error('Intercom Shutdown Error', err);
  }
}

function getAnalyticsUserId(user: UserSession): string {
  if (user.type === 'guest') {
    return `${user.customerId}+${user.jwtUser.email}`;
  }
  return user.jwtUser.sub;
}

export function initIntercom(
  intercom: Intercom | undefined,
  user: UserSession,
  impersonator: ImpersonatorSession | undefined,
): void {
  if (!intercom) {
    return;
  }

  try {
    const sessionToUse = impersonator ?? user;

    const createdAt = user.profile.createdAt ? Date.parse(user.profile.createdAt) : undefined;
    const unixCreatedAt = createdAt && !isNaN(createdAt) ? Math.floor(createdAt / 1000) : undefined;
    const initTime = Math.floor(Date.now() / 1000);

    // See https://developers.intercom.com/installing-intercom/docs/javascript-api-attributes-objects
    intercom('boot', {
      app_id: settings.intercom_id,
      email: user.jwtUser.email,
      created_at: unixCreatedAt,
      user_id: getAnalyticsUserId(user),
      user_hash: user.clientSettings.web.intercomUserHash,
      alignment: 'left',
      horizontal_padding: 3,
      vertical_padding: 3,
      language_override: sessionToUse.profile.preferences?.locale || DEFAULT_LOCALE,
      cd_web_version: version,
      vd_web_built_at: __BUILD_TIME__,
      support_access_enabled: sessionToUse.profile.isSupportAccessEnabled,
      last_seen_on_vd: initTime,
      ...remapUserFeatures(user.profile.features),
    });
  } catch (err) {
    console.error('Intercom Init Error', err);
  }
}

function convertToIntercomData(
  name: string,
  properties: Record<string, unknown> | undefined = {},
): { name: string; properties: Record<string, unknown> } {
  const formattedProperties: Record<string, unknown> = {};
  for (const key of Object.keys(properties)) {
    formattedProperties[kebabCase(key)] = properties[key];
  }

  return {
    name: kebabCase(name),
    properties: formattedProperties,
  };
}

export function trackEventIntercom(event: Readonly<AnalyticsEvent>): void {
  const { trackIntercom, name, properties } = event;

  if (trackIntercom && isIntercomLoaded(intercomInstance)) {
    try {
      const { name: eventName, properties: eventProperties } = convertToIntercomData(
        name,
        properties,
      );

      intercomInstance('trackEvent', eventName, eventProperties);
    } catch (err) {
      console.error('Intercom Event Error', err);
    }
  }
}
